import { useState } from "react";

import { useForm } from "react-hook-form";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid } from "@mui/material";
import { yupResolver } from '@hookform/resolvers/yup';
import { InputController } from "@shared/ui/form";
import i18next from "i18next";
import InputDatePickerController from "@shared/ui/form/component/InputDatePickerController";
import InputNumberFormatController from "@shared/ui/form/component/inputNumberFormatController";
import UploadFile from "@vendor/examples/UploadFile";
import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";

import { sendManualInvoicePublic } from "../api/invoiceApi";
import { sendManualPublicSchema } from "../schema/sendManualPublicSchema";
import { isAllowedInvoiceFileType, isWithinInvoiceFileSize } from "../lib/invoiceFileRules";
import { buildPublicManualInvoiceFormData } from "../lib/invoicePayloads";

import type { ChangeEvent } from "react";
import type { ManualInvoiceFormValues } from "../lib/invoicePayloads";

export interface SendManualInvoicePublicProps {
    handleClose: () => void;
    open: boolean;
    notLoading: () => void;
    loading: () => void;
    token: string;
    id: number;
}

interface ValidateFileState {
    required: boolean;
    type: boolean;
    size: boolean;
}

const validationFileInitial: ValidateFileState = {
    required: false,
    type: false,
    size: false
}

export const SendManualInvoicePublic = ({handleClose, open, notLoading, loading, token, id}: SendManualInvoicePublicProps) => {
    const {control, handleSubmit, reset, formState: {errors}} = useForm<ManualInvoiceFormValues>({
        resolver: yupResolver(sendManualPublicSchema) as never,
    });
    // `null` (no solo `undefined`) porque `onClose` más abajo llama `setFile(null)` (a
    // diferencia de los otros diálogos de carga, que usan `setFile()`); se preserva tal cual.
    const [file, setFile] = useState<File | null | undefined>()
    const [validateFile, setValidateFile] = useState<ValidateFileState>(validationFileInitial)

    const resetField = () => {
        // BUG PREEXISTENTE (no se corrige en esta anotación): este formulario no tiene campos
        // `firstName`/`lastName`/`email` (son `ccEmail`, `invoiceNumber`, `invoiceDate`,
        // `comments`, `itbmsTax`, `subtotal`, `total`, ver JSX de abajo), así que este `reset`
        // nunca limpia ninguno de los campos reales tras cerrar el diálogo. El `as never` solo
        // preserva el tipado tal cual venía; el comportamiento no cambia acá.
        reset({
            firstName: "",
            lastName: "",
            email: ""
        } as never)
    }

    const validateSizeFile = (files: File | null) => {
        const tooLarge = !isWithinInvoiceFileSize(files)
        setValidateFile({ required: false, type: false, size: tooLarge })
        return tooLarge
    }

    const validateTypeFile = (files: File | null) => {
        const allowed = isAllowedInvoiceFileType(files)
        setValidateFile({ required: false, type: !allowed, size: false })
        return allowed
    }

    const requiredFile = () => {
        setValidateFile({
            type: false,
            size: false,
            required: true,
        })
    }

    const handleChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
        // Comportamiento preexistente (ver mismo patrón en usePermission.ts): `files` puede ser
        // `null` en el tipo del DOM, pero el runtime siempre lo llena en este flujo de carga.
        if (e.target.files!.length) {
            // `length` distinto de cero garantiza que el primer elemento existe.
            const selectedFile = e.target.files![0]!
            let isFileType = validateTypeFile(selectedFile)
            if (isFileType) {
                let isFileSize = validateSizeFile(selectedFile)
                if (!isFileSize) {
                    setFile(selectedFile)
                }
            }
            return null
        }
        requiredFile()
    };

    const onClose = () => {
        setFile(null)
        setValidateFile(validationFileInitial)
        resetField()
        handleClose()
    }

    const onRegister = (value: ManualInvoiceFormValues) => {
        if (file === undefined) {
            requiredFile()
            return null;
        }

        if (!validateTypeFile(file) && validateSizeFile(file)) {
            return null;
        }

        loading()
        onClose()
        // `file` puede ser `null` acá (ver comentario del `useState` de arriba); comportamiento
        // preexistente sin guard nuevo, el cast documenta el paso tal cual venía.
        const formData = buildPublicManualInvoiceFormData(value, file as File)

        sendManualInvoicePublic(token, id, formData).then(() => {
            notLoading()
            SuccessAlert("Cargar de Factura", "Realizada con Exito").then()
        }).catch((error) => {
            const data = error.response?.data;
            notLoading()
            ErrorAlert("Error al Cargar Factura", data?.message ?? i18next.t("internalError")).then()
        })
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <form onSubmit={handleSubmit(onRegister)}>
                    <DialogTitle>Carga Manual de Factura</DialogTitle>
                    <DialogContent>
                        <Box sx={{p: 1}}>
                            <Grid container spacing={3}>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputController name="invoiceNumber" control={control}
                                                         type="string"
                                                         defaultValue="" label="Numero de factura"/>
                                        {errors.invoiceNumber &&
                                        <span
                                            style={{fontSize: 12, color: "red"}}>{errors.invoiceNumber.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        {/* `type` no es una prop de `InputNumberFormatController` (nunca se leía en
                                            runtime); se quita acá porque JSX ahora la rechaza, sin cambio de comportamiento. */}
                                        <InputNumberFormatController name="subtotal" control={control}
                                                                     defaultValue="0" label="Subtotal"/>
                                        {errors.subtotal &&
                                        <span style={{fontSize: 12, color: "red"}}>{errors.subtotal.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputNumberFormatController name="itbmsTax" control={control}
                                                                     defaultValue="0" label="ITBMS"/>
                                        {errors.itbmsTax &&
                                        <span style={{fontSize: 12, color: "red"}}>{errors.itbmsTax.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputNumberFormatController name="total" control={control}
                                                                     defaultValue="0" label="Total"/>
                                        {errors.total &&
                                        <span style={{fontSize: 12, color: "red"}}>{errors.total.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputController name="ccEmail" control={control}
                                                         type="email"
                                                         defaultValue="" label="Correo de Copia"/>
                                        {errors.ccEmail &&
                                        <span style={{fontSize: 12, color: "red"}}>{errors.ccEmail.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputDatePickerController
                                            control={control}
                                            defaultValue={new Date()}
                                            openTo='month'
                                            name="invoiceDate"
                                            label="Fecha de Factura"
                                            views={['year', 'month', 'day']}
                                        />
                                        {errors.invoiceDate &&
                                        <span style={{fontSize: 12, color: "red"}}>{errors.invoiceDate.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputController name="comments" control={control}
                                                         type="string"
                                                         defaultValue="" label="Comentario"/>
                                        {errors.comments &&
                                        <span
                                            style={{fontSize: 12, color: "red"}}>{errors.comments.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{mb: 2}}>
                                        <UploadFile
                                            id="coverPage"
                                            nameInput="cover"
                                            onChangeFile={handleChangeFile}
                                            file={file}
                                            textButton="Cargar Factura"
                                            fileLoaded="Factura Cargada"
                                        />
                                        {validateFile.size && <span style={{fontSize: 12, color: "red"}}>El archivo no debe exceder los 3 MB</span>}
                                        {validateFile.type && <span style={{fontSize: 12, color: "red"}}>Solo debe ser formato (.pdf o .docx)</span>}
                                        {validateFile.required &&
                                        <span style={{fontSize: 12, color: "red"}}>El campo es requerido</span>}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Cancelar</Button>
                        <Button type="submit">Cargar</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
